// src/components/ScheduleE.js
import React, { useState } from 'react';
import { Home, PlusCircle, Trash2, AlertTriangle, Edit2, ChevronDown, ChevronRight } from 'react-feather';

const PROPERTY_TYPES = [
  { code: '1', label: 'Single Family Residence' },
  { code: '2', label: 'Multi-Family Residence' },
  { code: '3', label: 'Vacation/Short-Term Rental' },
  { code: '4', label: 'Commercial' },
  { code: '5', label: 'Land' },
  { code: '6', label: 'Royalties' },
  { code: '7', label: 'Self-Rental' },
  { code: '8', label: 'Other' }
];

function ScheduleE({ data, onUpdate, formData, onDataChange }) {
  const [activeTab, setActiveTab] = useState('Part I');
  const [activePropIndex, setActivePropIndex] = useState(0);
  
  // --- NEW: State for toggling the detailed form ---
  const [showDetails, setShowDetails] = useState(false);

  const handleCheckbox = (field, value) => onUpdate({ ...data, [field]: value });

  const updateProp = (field, value) => {
    const newProps = [...data.properties];
    newProps[activePropIndex] = { ...newProps[activePropIndex], [field]: value };
    onUpdate({ ...data, properties: newProps });
  };

  const addProperty = () => {
    const newProp = { id: `prop-${Date.now()}`, address: 'New Property', propertyType: '1', fairRentalDays: 0, personalUseDays: 0, rentsReceived: 0, royaltiesReceived: 0, otherExpenses: [] };
    onUpdate({ ...data, properties: [...data.properties, newProp] });
    setActivePropIndex(data.properties.length);
    setShowDetails(true);
  };

  const removeProperty = (e, index) => {
    e.stopPropagation();
    const newProps = data.properties.filter((_, i) => i !== index);
    onUpdate({ ...data, properties: newProps });
    setActivePropIndex(0);
  };

  const updatePartnership = (index, field, value) => {
    const newParts = [...data.partnerships];
    newParts[index] = { ...newParts[index], [field]: value };
    onUpdate({ ...data, partnerships: newParts });
  };

  const addPartnership = () => {
      onUpdate({ ...data, partnerships: [...data.partnerships, { id: Date.now(), name: '', type: 'P' }] });
      setShowDetails(true);
  };

  const currentProp = data.properties[activePropIndex] || {};
  
  // Vacation Home Logic (Line 2)
  const isVacationHome = (parseFloat(currentProp.personalUseDays) || 0) > 14 || (parseFloat(currentProp.personalUseDays) || 0) > ((parseFloat(currentProp.fairRentalDays) || 0) * 0.10);

  // Calculate Total Net Income from Properties (Simple approximation for display)
  const calculatedTotalRental = data.properties.reduce((sum, prop) => {
      const income = (parseFloat(prop.rentsReceived) || 0) + (parseFloat(prop.royaltiesReceived) || 0);
      const expenses = (parseFloat(prop.advertising)||0) + (parseFloat(prop.insurance)||0) + (parseFloat(prop.mortgageInterest)||0) + (parseFloat(prop.repairs)||0) + (parseFloat(prop.taxes)||0) + (parseFloat(prop.depreciation)||0);
      return sum + (income - expenses);
  }, 0);

  return (
    <div className="form-section" style={{ marginTop: '2rem' }}>
      <div className="content-box-header">
        <h2><Home size={20} className="icon-orange" /> Supplemental Income</h2>
      </div>

      {/* --- QUESTION CARD --- */}
      <div className="dependent-card" style={{ marginBottom: '1.5rem' }}>
        <div className="form-group">
          <label style={{ fontWeight: 600, color: '#554333ff' }}>Do you have rental properties, royalties, or K-1 income?</label>
          <p style={{ fontSize: '0.9rem', color: '#8b7b64ff', marginBottom: '0.75rem' }}>
            Answer <strong>Yes</strong> if you need to list specific properties or partnerships. Answer <strong>No</strong> if you just want to enter a total Net Income/Loss.
          </p>
          <div className="radio-group">
            <input 
              type="radio" 
              id="schedE_simple" 
              checked={!showDetails} 
              onChange={() => setShowDetails(false)} 
            />
            <label htmlFor="schedE_simple">No (Enter Net Total Only)</label>

            <input 
              type="radio" 
              id="schedE_detailed" 
              checked={showDetails} 
              onChange={() => setShowDetails(true)} 
            />
            <label htmlFor="schedE_detailed">Yes (List Properties & K-1s)</label>
          </div>
        </div>
      </div>

      {/* --- SIMPLE VIEW (Manual Override Only) --- */}
      {!showDetails && (
        <div className="dependent-card" style={{ animation: 'fadeIn 0.3s' }}>
            <div className="form-group">
                <label style={{ fontWeight: 600, color: '#554933ff' }}>Net Rental Income (Schedule 1, Line 5)</label>
                <div className="input-with-prefix">
                    <span className="prefix">$</span>
                    <input 
                        type="number" 
                        name="manualRentalIncome" 
                        value={formData?.manualRentalIncome ?? ''} 
                        onChange={onDataChange} 
                        placeholder="0" 
                    />
                </div>
                <small>Enter the total Net Income (or Loss) from all rental properties.</small>
            </div>
        </div>
      )}

      {/* --- DETAILED VIEW (Full Form) --- */}
      {showDetails && (
        <div style={{ animation: 'fadeIn 0.3s' }}>
            
            {/* Summary Header for Detailed View */}
            <div className="dependent-card" style={{ marginBottom: '1.5rem', border: '2px solid var(--accent-primary)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <Edit2 size={16} className="icon-blue" />
                    <label style={{ fontWeight: 600, color: '#554833ff', margin: 0 }}>Net Rental Income Summary</label>
                </div>
                
                <div className="form-row" style={{ alignItems: 'flex-end' }}>
                    <div className="form-group" style={{ flex: 1 }}>
                        <label>Calculated from Details Below</label>
                        <input 
                            type="text" 
                            value={`$${calculatedTotalRental.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} 
                            readOnly 
                            style={{ backgroundColor: '#f9f6f1ff', color: '#8b7b64ff', fontWeight: 'bold' }} 
                        />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                        <label style={{ color: '#9a3412' }}>Manual Override (Optional)</label>
                        <div className="input-with-prefix">
                            <span className="prefix">$</span>
                            <input 
                                type="number" 
                                name="manualRentalIncome" 
                                value={formData?.manualRentalIncome ?? ''} 
                                onChange={onDataChange} 
                                placeholder="0" 
                                style={{ borderColor: '#fed7aa' }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="dependent-card" style={{ marginBottom: '1.5rem', backgroundColor: '#fff7ed', border: '1px solid #fed7aa' }}>
                <div className="form-group checkbox-group">
                <input type="checkbox" checked={data.paymentsRequiring1099} onChange={(e) => handleCheckbox('paymentsRequiring1099', e.target.checked)} />
                <label style={{ color: '#9a3412' }}>Did you make payments requiring Form(s) 1099?</label>
                </div>
                {data.paymentsRequiring1099 && (
                <div className="form-group checkbox-group" style={{ marginLeft: '20px', marginTop: '0.5rem' }}>
                    <input type="checkbox" checked={data.didFile1099} onChange={(e) => handleCheckbox('didFile1099', e.target.checked)} />
                    <label style={{ color: '#9a3412' }}>If "Yes," did you file them?</label>
                </div>
                )}
            </div>

            {/* --- MAIN TABS (Real Estate vs Partnerships) --- */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '1.5rem', borderBottom: '1px solid #f0e9e2ff' }}>
                {['Part I', 'Part II'].map(tab => (
                <button 
                    key={tab} 
                    onClick={() => setActiveTab(tab)}
                    style={{ 
                        background: 'none', 
                        border: 'none', 
                        borderBottom: activeTab === tab ? '3px solid #EB8258' : '3px solid transparent',
                        color: activeTab === tab ? '#EB8258' : '#8b7a64ff',
                        fontWeight: activeTab === tab ? '700' : '500',
                        padding: '10px 5px',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        transition: 'all 0.2s'
                    }}
                >
                    {tab === 'Part I' ? 'Real Estate' : 'Partnerships & S-Corps'}
                </button>
                ))}
            </div>

            {activeTab === 'Part I' && (
                <>
                {/* --- PROPERTY SELECTOR BUTTONS --- */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '1.5rem', alignItems: 'center' }}>
                    {data.properties.map((prop, idx) => {
                    const isActive = idx === activePropIndex;
                    return (
                        <div key={prop.id} style={{ position: 'relative' }}>
                        <button 
                            onClick={() => setActivePropIndex(idx)} 
                            style={{ 
                            padding: '8px 35px 8px 16px', // Extra right padding for delete icon
                            borderRadius: '6px',
                            border: isActive ? '1px solid #EB8258' : '1px solid #f0eae2ff',
                            backgroundColor: isActive ? '#fff7ed' : '#ffffff',
                            color: isActive ? '#9a3412' : '#8b7b64ff',
                            fontWeight: isActive ? '600' : 'normal',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                            }}
                        >
                            {prop.address || `Property ${idx + 1}`}
                        </button>
                        <button 
                            onClick={(e) => removeProperty(e, idx)} 
                            style={{ 
                            position: 'absolute', 
                            right: '8px', 
                            top: '50%', 
                            transform: 'translateY(-50%)', 
                            background: 'none', 
                            border: 'none', 
                            color: '#ef4444', 
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                            }}
                            title="Remove Property"
                        >
                            <Trash2 size={14} />
                        </button>
                        </div>
                    );
                    })}
                    <button 
                        onClick={addProperty} 
                        className="add-item-button" 
                        style={{ padding: '8px 12px', height: '38px' }}
                    >
                        <PlusCircle size={16} /> 
                        <span style={{ marginLeft: '5px' }}>Add Property</span>
                    </button>
                </div>

                {data.properties.length > 0 ? (
                    <div className="dependent-card">
                    <div className="form-row">
                        <div className="form-group" style={{ flex: 2 }}>
                            <label>Address</label>
                            <input type="text" value={currentProp.address} onChange={e => updateProp('address', e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label>Type</label>
                            <select value={currentProp.propertyType} onChange={e => updateProp('propertyType', e.target.value)}>
                                {PROPERTY_TYPES.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
                            </select>
                        </div>
                        {currentProp.propertyType === '8' && (
                        <input 
                            type="text" 
                            placeholder="Description (e.g., Billboard, Boat)" 
                            value={currentProp.otherPropertyDescription || ''} 
                            onChange={e => updateProp('otherPropertyDescription', e.target.value)}
                            style={{ marginTop: '0.5rem', borderColor: '#f59e0b' }}
                        />
                        )}
                    </div>
                    <div className="form-row">
                        <div className="form-group"><label>Fair Rental Days</label><input type="number" value={currentProp.fairRentalDays} onChange={e => updateProp('fairRentalDays', e.target.value)} /></div>
                        <div className="form-group"><label>Personal Use Days</label><input type="number" value={currentProp.personalUseDays} onChange={e => updateProp('personalUseDays', e.target.value)} /></div>
                        <div className="form-group checkbox-group" style={{ alignSelf: 'center' }}><input type="checkbox" checked={currentProp.qjv} onChange={e => updateProp('qjv', e.target.checked)} /><label>QJV Election?</label></div>
                    </div>
                    {isVacationHome && <div style={{ padding: '10px', backgroundColor: '#fff1f2', color: '#be123c', borderRadius: '5px', marginBottom: '1rem', fontSize: '0.9rem' }}><AlertTriangle size={16} /> <strong>Vacation Home Rule:</strong> Personal use is high. Losses are disallowed.</div>}
                    
                    <h5 style={{ borderTop: '1px dashed #ccc', paddingTop: '1rem', marginTop: '1rem', marginBottom: '1rem' }}>Income & Expenses</h5>
                    <div className="form-row">
                        <div className="form-group"><label>Rents Received</label><input type="number" value={currentProp.rentsReceived} onChange={e => updateProp('rentsReceived', e.target.value)} /></div>
                        <div className="form-group"><label>Royalties</label><input type="number" value={currentProp.royaltiesReceived} onChange={e => updateProp('royaltiesReceived', e.target.value)} /></div>
                    </div>
                    
                    {/* Auto & Travel Box */}
                    <div className="form-group" style={{ backgroundColor: '#fff7ed', padding: '1rem', borderRadius: '6px', border: '1px solid #fed7aa', marginBottom: '1rem' }}>
                        <label style={{ fontWeight: '600', color: '#9a3412', marginBottom: '0.5rem', display: 'block' }}>Auto & Travel (Line 6)</label>
                        <div className="form-row">
                        <div className="form-group"><label style={{ fontSize: '0.85rem' }}>Miles (70¢/mile)</label><input type="number" value={currentProp.autoMiles} onChange={e => updateProp('autoMiles', e.target.value)} /></div>
                        <div className="form-group"><label style={{ fontSize: '0.85rem' }}>Other Travel</label><input type="number" value={currentProp.autoAndTravel} onChange={e => updateProp('autoAndTravel', e.target.value)} /></div>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group"><label>Advertising</label><input type="number" value={currentProp.advertising} onChange={e => updateProp('advertising', e.target.value)} /></div>
                        <div className="form-group"><label>Insurance</label><input type="number" value={currentProp.insurance} onChange={e => updateProp('insurance', e.target.value)} /></div>
                        <div className="form-group"><label>Mortgage Interest</label><input type="number" value={currentProp.mortgageInterest} onChange={e => updateProp('mortgageInterest', e.target.value)} /></div>
                    </div>
                    <div className="form-row">
                        <div className="form-group"><label>Repairs</label><input type="number" value={currentProp.repairs} onChange={e => updateProp('repairs', e.target.value)} /></div>
                        <div className="form-group"><label>Taxes</label><input type="number" value={currentProp.taxes} onChange={e => updateProp('taxes', e.target.value)} /></div>
                        <div className="form-group"><label>Depreciation</label><input type="number" value={currentProp.depreciation} onChange={e => updateProp('depreciation', e.target.value)} /></div>
                    </div>
                    </div>
                ) : <p className="empty-state-message">No properties added.</p>}
                </>
            )}

            {activeTab === 'Part II' && (
                <div className="dependent-card">
                <div className="content-box-header">
                    <h4>Partnerships & S-Corps</h4>
                    <button onClick={addPartnership} className="add-item-button"><PlusCircle size={14} /> <span>Add K-1</span></button>
                </div>
                {data.partnerships.map((k1, idx) => (
                    <div key={k1.id} style={{ borderBottom: '1px solid #f0eae2ff', paddingBottom: '1.5rem', marginBottom: '1.5rem' }}>
                    <div className="form-row">
                        <div className="form-group" style={{ flex: 2 }}><label>Entity Name</label><input type="text" value={k1.name} onChange={e => updatePartnership(idx, 'name', e.target.value)} /></div>
                        <div className="form-group"><label>Type</label><select value={k1.type} onChange={e => updatePartnership(idx, 'type', e.target.value)}><option value="P">Partnership</option><option value="S">S-Corp</option></select></div>
                        <div className="form-group">
                            <label>EIN (Col d)</label>
                            <input type="text" value={k1.ein} onChange={e => updatePartnership(idx, 'ein', e.target.value)} />
                        </div>
                    </div>
                    
                    <div className="form-row" style={{ marginBottom: '1rem' }}>
                        <div className="form-group checkbox-group">
                            <input 
                                type="checkbox" 
                                checked={k1.isForeign || false} 
                                onChange={e => updatePartnership(idx, 'isForeign', e.target.checked)} 
                            />
                            <label>Foreign Partnership? (Col c)</label>
                        </div>
                        <div className="form-group checkbox-group">
                            <input 
                                type="checkbox" 
                                checked={k1.basisComputationRequired || false} 
                                onChange={e => updatePartnership(idx, 'basisComputationRequired', e.target.checked)} 
                            />
                            <label>Basis Computation Required? (Col e)</label>
                        </div>
                        <div className="form-group checkbox-group">
                            <input 
                                type="checkbox" 
                                checked={k1.notAtRisk || false} 
                                onChange={e => updatePartnership(idx, 'notAtRisk', e.target.checked)} 
                            />
                            <label>Not At Risk? (Col f)</label>
                        </div>
                    </div>
                    
                    <div className="form-row" style={{ backgroundColor: '#fcfaf8ff', padding: '1rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                        <div className="form-group"><label>Passive Loss Allowed</label><input type="number" value={k1.passiveLossAllowed} onChange={e => updatePartnership(idx, 'passiveLossAllowed', e.target.value)} placeholder="Loss (positive)" /></div>
                        <div className="form-group"><label>Passive Income</label><input type="number" value={k1.passiveIncome} onChange={e => updatePartnership(idx, 'passiveIncome', e.target.value)} /></div>
                        <div className="form-group"><label>Non-Passive Income</label><input type="number" value={k1.nonPassiveIncome} onChange={e => updatePartnership(idx, 'nonPassiveIncome', e.target.value)} /></div>
                    </div>
                    </div>
                ))}
                </div>
            )}
        </div>
      )}
    </div>
  );
}
export default ScheduleE;